import {
    filter,
    find,
    flatMap,
    includes,
    isEmpty,
    isNumber,
    rangeRight,
    round,
    some,
    uniqBy,
} from "lodash";
import { WEEK_DAYS } from "../../../common/constants";
import { WeekDay } from "../../../common/types";
import {
    DEVICE_TYPES,
    RADIATOR_THERMOSTAT_TYPES,
} from "../../devices/constants";
import { Device } from "../../devices/models";
import { ACTIONS, ENERGY_UNIT, EVENTS } from "../constants";
import { EnergySettings } from "../models/energySettings.model";
import { Mode } from "../models/mode.model";
import { RulesetAssignmentRequest } from "../models/rulesetAssignmentRequest.model";
import { Action, EnergyUnitType, Event } from "../types";

export const isWeekDay = (value: any): value is WeekDay =>
    Object.values(WEEK_DAYS).includes(value);

export const isEvent = (value: any): value is Event =>
    Object.values(EVENTS).includes(value);

export const isAction = (value: any): value is Action =>
    Object.values(ACTIONS).includes(value);

export const formatCelsius = (value: number): string => `${value}°C`;

export const getTemperatureOption = (tempValue: number) => ({
    key: tempValue,
    label: formatCelsius(tempValue),
});

const getDecimalPlaces = (num: number) => {
    const decimalPart = (num.toString().split(".")[1] || "").length;
    return decimalPart;
};

export const generateTemperatureOptions = (
    minTemp: number,
    maxTemp: number,
    step?: number
) => {
    if (!step || Number.isInteger(step)) {
        return rangeRight(minTemp, maxTemp + 1, step).map(getTemperatureOption);
    }
    return rangeRight(minTemp, maxTemp + step, step)
        .map((value) => round(value, getDecimalPlaces(step)))
        .map(getTemperatureOption);
};

export const getModeDeviceStatus = (mode: Mode | null, devices: Device[]) => {
    if (!mode || isEmpty(devices)) {
        return { hasFanCoils: false, hasRadiatorThermostats: false };
    }

    const filteredDevices: Device[] = filter(devices, (device: Device) =>
        includes(mode?.locationIds, device.locationId)
    );

    const hasFanCoils = some(filteredDevices, [
        "deviceType",
        DEVICE_TYPES.FAN_COIL_THERMOSTAT,
    ]);

    const hasRadiatorThermostats = some(filteredDevices, (device: Device) =>
        includes(RADIATOR_THERMOSTAT_TYPES, device.deviceType)
    );

    return { hasFanCoils, hasRadiatorThermostats };
};

export const convertEnergyUnit = (
    value: number | undefined,
    outputUnit: EnergyUnitType
): number => {
    if (!isNumber(value)) {
        throw new Error("Invalid value specified.");
    }
    switch (outputUnit) {
        case ENERGY_UNIT.KILO:
            return value / 1000;
        case ENERGY_UNIT.MEGA:
            return value / 1000000;
        default:
            throw new Error(
                "Invalid output unit specified. Supported units are 'KILO' and 'MEGA'."
            );
    }
};

export const convertWattHoursToKilo = (
    wattHours: number | undefined
): number | null =>
    isNumber(wattHours)
        ? round(convertEnergyUnit(wattHours, ENERGY_UNIT.KILO), 3)
        : null;

export const getEnergySettingValue = (
    energySettings: EnergySettings | undefined,
    locationId: string | undefined,
    key: string
): number | undefined => {
    if (locationId) {
        const location = find(energySettings?.locations, { id: locationId });
        const setting = find(location?.settings, { key });
        if (setting && !setting.useCompanyValue && isNumber(setting.value)) {
            return setting.value;
        }
    }
    const generalSetting = find(energySettings?.settings, { key });
    return isNumber(generalSetting?.value) ? generalSetting?.value : undefined;
};

export const getPurposes = (ruleset?: RulesetAssignmentRequest) => {
    const allSpacePurposes = flatMap(
        ruleset?.locations,
        (location) => location.spacePurposes || []
    );

    const allSubSpacePurposes = flatMap(
        ruleset?.locations,
        (location) => location.subSpacePurposes || []
    );

    const uniqueSpacePurposes = uniqBy(allSpacePurposes, "id");
    const uniqueSubSpacePurposes = uniqBy(allSubSpacePurposes, "id");

    return {
        spacePurposes: uniqueSpacePurposes,
        subSpacePurposes: uniqueSubSpacePurposes,
    };
};
